import type { Balance, SegmentDef, SegmentOf, TrackDef } from '@runkr/config'
import {
  barrelTables,
  bladeTables,
  boundsOf,
  fallingBlockTables,
  spikeTables,
  staticTables,
  trapdoorTables,
} from '../hazards/tables'
import type { HazardTables } from '../hazards/tables'
import type {
  Aabb,
  Checkpoint,
  CompiledTrack,
  HazardDef,
  HazardKind,
  Platform,
  Ramp,
  Solid,
  Zone,
} from './types'
import { indexTrack } from './xindex'

type Hazardous = SegmentOf<
  | 'swinging_blade'
  | 'log_ram'
  | 'spike_strip'
  | 'falling_block'
  | 'boulder_drop'
  | 'trapdoor'
  | 'stake_barricade'
  | 'spiked_stone'
  | 'barrel_roll'
>

/** Stable sort by `x` (ties keep authored order). */
function byX<T extends { readonly x: number }>(items: T[]): T[] {
  return items.sort((a, b) => a.x - b.x)
}

function box(s: Aabb): Aabb {
  return { x: s.x, y: s.y, w: s.w, h: s.h }
}

/**
 * Hazard tables by segment type. `log_ram` and `boulder_drop` are the blade and falling block
 * under their Sky Meadow names; static hazards get a one-entry cycle.
 */
function compileHazard(seg: Hazardous, cfg: Balance): HazardDef {
  const hz = cfg.hazards
  let kind: HazardKind
  let stunTicks: number
  let tables: HazardTables
  let periodTicks = 1
  let phaseTicks = 0
  switch (seg.type) {
    case 'swinging_blade':
    case 'log_ram':
      kind = 'blade'
      stunTicks = hz.bladeStunTicks
      tables = bladeTables(seg, hz.bladeTipSizePx)
      break
    case 'spike_strip':
      kind = 'spike'
      stunTicks = hz.spikeStunTicks
      tables = spikeTables(seg, hz)
      break
    case 'falling_block':
    case 'boulder_drop':
      kind = 'fallingBlock'
      stunTicks = hz.fallingBlockStunTicks
      tables = fallingBlockTables(seg, hz, cfg)
      break
    case 'trapdoor':
      kind = 'trapdoor'
      stunTicks = hz.trapdoorStunTicks
      tables = trapdoorTables(seg, hz)
      break
    case 'stake_barricade': {
      kind = 'stakeBarricade'
      stunTicks = hz.stakeStunTicks
      const h = hz.stakeHeightPx
      tables = staticTables({ x: seg.x, y: seg.y - h, w: seg.w, h })
      break
    }
    case 'barrel_roll':
      kind = 'barrelRoll'
      stunTicks = hz.barrelStunTicks
      tables = barrelTables(seg, hz)
      break
    case 'spiked_stone': {
      kind = 'spikedStone'
      stunTicks = hz.spikedStoneStunTicks
      const s = hz.spikedStoneSizePx
      tables = staticTables({ x: seg.x, y: seg.y - s, w: s, h: s })
      break
    }
  }
  if ('periodTicks' in seg) {
    periodTicks = seg.periodTicks
    phaseTicks = seg.phaseTicks
  }
  return {
    ...boundsOf(tables.boxes),
    kind,
    anchorX: seg.x,
    anchorY: seg.y,
    periodTicks,
    phaseTicks,
    stunTicks,
    phases: tables.phases,
    boxes: tables.boxes,
  }
}

/**
 * Coin positions of a shard trail: `count` along x at `spacing`; an arc rises to `arcHeight` at
 * the middle (parabola, rounded to whole px); a grid repeats the row `rows` times `spacing` apart
 * downwards. Pure arithmetic at load time.
 */
export function shardTrail(seg: SegmentOf<'shard_trail'>): Checkpoint[] {
  const out: Checkpoint[] = []
  const rows = seg.shape === 'grid' ? (seg.rows ?? 1) : 1
  const n = seg.count
  for (let r = 0; r < rows; r++) {
    for (let k = 0; k < n; k++) {
      const t = n > 1 ? k / (n - 1) : 0
      const lift = seg.shape === 'arc' ? Math.round(4 * (seg.arcHeight ?? 0) * t * (1 - t)) : 0
      out.push({ x: seg.x + k * seg.spacing, y: seg.y + r * seg.spacing - lift })
    }
  }
  return out
}

/**
 * Turn a validated track into the sim's flat arrays (ARCHITECTURE §2.6), each sorted by `x`, with
 * the x-bucket index and the hazard lookup tables. Pure; run once per track at load.
 * Line zones (bounce pads, speed and slow strips) have zero height: they match feet exactly on
 * `y`. Visual fields (`style`, `supports`, `decor`) are ignored.
 */
export function compileTrack(def: TrackDef, cfg: Balance): CompiledTrack {
  const solids: Solid[] = []
  const platforms: Platform[] = []
  const ramps: Ramp[] = []
  const zones: Zone[] = []
  const hazards: HazardDef[] = []
  const checkpoints: Checkpoint[] = []
  const crates: Checkpoint[] = []
  const coins: Checkpoint[] = []

  def.segments.forEach((seg: SegmentDef, i) => {
    try {
      switch (seg.type) {
        case 'ground':
          solids.push(box(seg))
          break
        case 'obstacle':
          solids.push({ ...box(seg), obstacle: true })
          break
        case 'gap':
          break
        case 'ramp':
          ramps.push({ ...box(seg), dir: seg.dir })
          break
        case 'moving_platform':
          platforms.push({
            kind: 'moving',
            ...box(seg),
            dx: seg.dx,
            dy: seg.dy,
            periodTicks: seg.periodTicks,
          })
          break
        case 'crumbling_platform':
          platforms.push({ kind: 'crumbling', ...box(seg) })
          break
        case 'platform':
          platforms.push({ kind: 'static', ...box(seg) })
          break
        case 'bounce_pad':
          zones.push({ kind: 'bouncePad', x: seg.x, y: seg.y, w: seg.w, h: 0 })
          break
        case 'speed_strip':
          zones.push({ kind: 'speedStrip', x: seg.x, y: seg.y, w: seg.w, h: 0 })
          break
        case 'slow_strip':
          zones.push({ kind: 'slowStrip', x: seg.x, y: seg.y, w: seg.w, h: 0 })
          break
        case 'mud':
          zones.push({ kind: 'mud', ...box(seg) })
          break
        case 'swinging_blade':
        case 'log_ram':
        case 'spike_strip':
        case 'falling_block':
        case 'boulder_drop':
        case 'trapdoor':
        case 'stake_barricade':
        case 'spiked_stone':
        case 'barrel_roll':
          hazards.push(compileHazard(seg, cfg))
          break
        case 'crate':
        case 'item_box':
          crates.push({ x: seg.x, y: seg.y })
          break
        case 'coin':
          coins.push({ x: seg.x, y: seg.y })
          break
        case 'shard_trail':
          coins.push(...shardTrail(seg))
          break
        case 'decor':
          break // visual only
        case 'checkpoint':
          checkpoints.push({ x: seg.x, y: seg.y })
          break
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      throw new Error(`compileTrack ${def.id}: segments.${i} (${seg.type}): ${msg}`, { cause: err })
    }
  })

  return indexTrack({
    solids: byX(solids),
    platforms: byX(platforms),
    ramps: byX(ramps),
    zones: byX(zones),
    checkpoints: byX(checkpoints),
    hazards: byX(hazards),
    crates: byX(crates),
    coins: byX(coins),
    finishX: def.finishX,
    killY: def.killY,
  })
}
